import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { RxCross1 } from "react-icons/rx";
import { server, backend_url } from "../server";
import { getAllOrdersOfUser } from "../redux/actions/order";

const CLOSE_REASONS = [
  { value: "already_refunded", label: "I already received a refund" },
  { value: "received_replacement", label: "I received a replacement" },
  { value: "accidental", label: "I accidentally started a return" },
  { value: "no_longer_want_return", label: "I no longer want to return the item" },
  { value: "other", label: "Other" },
];

const formatCurrency = (value) => {
  if (!value && value !== 0) return "—";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(value));
};

/**
 * Buyer flow to close an open refund request (still under seller review).
 * Standalone page (no profile tabs), eBay-style copy and fields.
 */
const CancelRefundRequest = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { orders, isLoading } = useSelector((state) => state.order);
  const { user } = useSelector((state) => state.user);
  const userId = user?._id;

  const [reasonCode, setReasonCode] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (userId) dispatch(getAllOrdersOfUser(userId));
  }, [dispatch, userId]);

  const order = useMemo(
    () => orders?.find((o) => String(o._id) === String(id)),
    [orders, id]
  );

  const isBuyer = useMemo(() => {
    if (!order || !userId) return false;
    return String(order.user?._id || order.user) === String(userId);
  }, [order, userId]);

  const canWithdraw = useMemo(() => {
    if (!order || !isBuyer) return false;
    if (order.vaMediation?.status === "open" || order.vaMediation?.status === "mediating") return false;
    const lines = order.refunds && order.refunds.length > 0;
    if (lines) {
      return order.refunds.some((r) => r.status === "Processing refund");
    }
    if (
      order.buyerReportedIssueAt &&
      !order.payoutReleased &&
      !order.userReceivedConfirmed
    ) {
      return true;
    }
    return order.status === "Processing refund" && String(order.refundReason || "").trim();
  }, [order, isBuyer]);

  const primaryLine = order?.cart?.[0];
  const thumb = primaryLine?.images?.[0];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!order?._id || !reasonCode) {
      toast.info("Please choose a reason for closing this request.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${server}/order/buyer-withdraw-refund/${order._id}`,
        { reasonCode, note: note.trim() },
        { withCredentials: true }
      );
      toast.success("Your refund request has been closed.");
      if (userId) dispatch(getAllOrdersOfUser(userId));
      navigate(`/user/order/${order._id}`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not close this request.");
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading && !order) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center text-sm text-slate-500">
        Loading…
      </div>
    );
  }

  if (!isLoading && !order) {
    return (
      <div className="mx-auto max-w-lg px-4 py-12 text-center">
        <p className="text-slate-600">We could not find this order.</p>
        <Link to="/profile/orders" className="mt-4 inline-block text-sm font-semibold text-[#38513b] hover:underline">
          Back to orders
        </Link>
      </div>
    );
  }

  if (order && !isBuyer) {
    return (
      <div className="mx-auto max-w-lg px-4 py-12 text-center">
        <p className="text-slate-600">Only the buyer can close this refund request.</p>
        <Link to="/" className="mt-4 inline-block text-sm font-semibold text-[#38513b] hover:underline">
          Home
        </Link>
      </div>
    );
  }

  if (order && !canWithdraw) {
    return (
      <div className="mx-auto max-w-lg px-4 py-12">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm text-center">
          <p className="text-slate-800 font-medium">Nothing to close</p>
          <p className="mt-2 text-sm text-slate-600">
            This refund request is not waiting on the seller in a way you can cancel from here (for example, a return may already be in progress).
          </p>
          <Link
            to={`/user/order/${order._id}`}
            className="mt-6 inline-flex h-11 items-center justify-center rounded-xl bg-[#38513b] px-6 text-sm font-semibold text-white"
          >
            Back to order
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-8 sm:py-12">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-lg font-semibold text-slate-900 sm:text-xl">Close refund request</h1>
        <Link
          to={`/user/order/${id}`}
          className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
          aria-label="Close"
        >
          <RxCross1 size={22} />
        </Link>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="flex gap-3 border-b border-slate-100 pb-4">
          {thumb ? (
            <img
              src={String(thumb).startsWith("http") ? thumb : `${backend_url}/${String(thumb).replace(/^\//, "")}`}
              alt=""
              className="h-16 w-16 shrink-0 rounded-lg border border-slate-200 object-contain"
            />
          ) : (
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-xs text-slate-400">
              Item
            </div>
          )}
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-snug text-slate-900 line-clamp-3">
              {primaryLine?.name || "Order items"}
            </p>
            <p className="mt-1 text-xs text-slate-500">
              Order #{order?.orderNumber ?? order?._id?.toString().substring(0, 8) ?? "—"}
            </p>
            {primaryLine?.discountPrice != null && (
              <p className="mt-0.5 text-xs font-medium text-slate-700">{formatCurrency(primaryLine.discountPrice)}</p>
            )}
          </div>
        </div>

        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/90 px-3 py-3 text-sm text-amber-950">
          If you close this request, you can no longer continue this refund case with the seller here. Only close it if you are sure — for example you
          fixed the issue with the seller or you opened the request by mistake.
        </div>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <label htmlFor="close-reason" className="block text-sm font-semibold text-slate-900">
              Reason for closing
            </label>
            <select
              id="close-reason"
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
              className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-900 focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
              required
            >
              <option value="" disabled>
                Select a reason
              </option>
              {CLOSE_REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="close-note" className="block text-sm font-medium text-slate-700">
              Add a few more details <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <textarea
              id="close-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              maxLength={2000}
              placeholder="Give us a little more info."
              className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
            />
          </div>
          <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
            <Link
              to={`/user/order/${id}`}
              className="inline-flex h-11 items-center justify-center rounded-xl border border-slate-200 px-5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Keep request open
            </Link>
            <button
              type="submit"
              disabled={submitting || !reasonCode}
              className="inline-flex h-11 items-center justify-center rounded-xl bg-[#38513b] px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#2f4232] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? "Closing…" : "Close refund request"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CancelRefundRequest;
